"use client";

import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Knopf } from "../knopf";
import { KleinesModal } from "../modal";
import { Textfeld } from "../formular";
import {
  SymAufgabenliste,
  SymFett,
  SymKursiv,
  SymLink,
  SymLinkAus,
  SymListe,
  SymNummerierung,
  SymRueckgaengig,
  SymUeberschrift2,
  SymUeberschrift3,
  SymWiederholen,
} from "./symbole";

/**
 * Anzeigezustand der Leiste. Gleiche Form wie `HornLeistenZustand` aus `@horn/editor` (`useHornLeiste`); dieses Paket
 * hängt bewusst nicht von `@horn/editor` oder TipTap ab, die Form genügt (strukturelle Typen).
 */
export interface EditorLeistenZustand {
  bearbeitbar: boolean;
  leer: boolean;
  fett: boolean;
  kursiv: boolean;
  ueberschrift2: boolean;
  ueberschrift3: boolean;
  aufzaehlung: boolean;
  nummerierung: boolean;
  aufgabenliste: boolean;
  link: boolean;
  kannRueckgaengig: boolean;
  kannWiederholen: boolean;
}

/** Befehle der Leiste; gleiche Form wie `HornLeistenBefehle`. */
export interface EditorLeistenBefehle {
  fett: () => void;
  kursiv: () => void;
  ueberschrift: (ebene: 2 | 3) => void;
  aufzaehlung: () => void;
  nummerierung: () => void;
  aufgabenliste: () => void;
  rueckgaengig: () => void;
  wiederholen: () => void;
  linkAdresse: () => string;
  linkSetzen: (eingabe: string) => { ok: true } | { ok: false; grund: "leer" | "ungueltig" };
  linkEntfernen: () => void;
}

const GRUPPE_KLASSE = "flex items-center gap-x-0.5";
const TRENNER_KLASSE = "mx-1 h-5 border-s border-layer-line";
// Die Leiste bricht um (kein Querscroll): bei schmalem Platz stehen alle Werkzeuge im Bild, keines verdeckt (Nacharbeit prod-ui2).
const LEISTE_KLASSE = "flex flex-wrap items-center gap-x-0.5 gap-y-1 min-w-0 max-w-full";

/** Wortlaut der Rückmeldungen im Link-Dialog (r105: ein Satz, kein Code). */
export const LINK_SAETZE = {
  leer: "Bitte eine Adresse eingeben.",
  ungueltig: "Diese Adresse ist nicht gültig. Bitte eine Web-Adresse (https://…) oder eine E-Mail-Adresse eingeben.",
} as const;

/** Ein Knopf der Leiste: `Knopf` ghost, aktiv = sekundär, `aria-pressed` für Umschalter. */
function Werkzeug({
  label,
  symbol,
  aktiv,
  umschalter = true,
  gesperrt = false,
  onClick,
  titel,
}: {
  label: string;
  symbol: ReactNode;
  aktiv?: boolean;
  /** `false` für Aktionen ohne Dauerzustand (Rückgängig, Link öffnen): kein `aria-pressed`. */
  umschalter?: boolean;
  gesperrt?: boolean;
  onClick: () => void;
  titel?: string;
}) {
  return (
    <Knopf
      nurSymbol
      groesse="sm"
      variante={aktiv ? "sekundaer" : "ghost"}
      label={label}
      titel={titel ?? label}
      symbol={symbol}
      starr
      ariaPressed={umschalter ? Boolean(aktiv) : undefined}
      disabled={gesperrt}
      onClick={onClick}
      daten={{ "data-editor-werkzeug": true }}
    />
  );
}

/**
 * Roving tabindex der Leiste (WAI-ARIA toolbar): genau ein Knopf ist per Tab erreichbar, Pfeil links/rechts, Pos1 und Ende
 * wandern durch die bedienbaren Knöpfe. `Knopf` kennt kein `tabIndex`; das Attribut wird deshalb nach jedem Rendern am
 * Element gesetzt (die Knöpfe tragen es nicht selbst, React überschreibt es nicht).
 */
function useRovingLeiste() {
  const huelle = useRef<HTMLDivElement>(null);
  const aktuell = useRef(0);

  const knoepfe = () => Array.from(huelle.current?.querySelectorAll<HTMLButtonElement>("button[data-editor-werkzeug]") ?? []);

  useEffect(() => {
    const alle = knoepfe();
    const frei = alle.filter((k) => !k.disabled);
    if (frei.length === 0) return;
    const aktiv = frei.includes(alle[aktuell.current] as HTMLButtonElement) ? (alle[aktuell.current] as HTMLButtonElement) : frei[0]!;
    for (const k of alle) k.setAttribute("tabindex", k === aktiv ? "0" : "-1");
  });

  const beiFokus = () => {
    const alle = knoepfe();
    const i = alle.indexOf(document.activeElement as HTMLButtonElement);
    if (i >= 0) {
      aktuell.current = i;
      for (const k of alle) k.setAttribute("tabindex", k === alle[i] ? "0" : "-1");
    }
  };

  const beiTaste = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!["ArrowRight", "ArrowLeft", "Home", "End"].includes(e.key)) return;
    const frei = knoepfe().filter((k) => !k.disabled);
    if (frei.length === 0) return;
    const i = frei.indexOf(document.activeElement as HTMLButtonElement);
    let n = i;
    if (e.key === "ArrowRight") n = (i + 1) % frei.length;
    else if (e.key === "ArrowLeft") n = (i - 1 + frei.length) % frei.length;
    else if (e.key === "Home") n = 0;
    else n = frei.length - 1;
    e.preventDefault();
    frei[n]?.focus();
  };

  return { huelle, beiFokus, beiTaste };
}

/**
 * Werkzeugleiste der Editor-Einfassung (G5 Variante A, Bauplan E2): Überschrift 2 und 3, Fett, Kursiv, Liste,
 * Nummerierung, Aufgabenliste, Link, Rückgängig, Wiederholen. `role="toolbar"`, Knöpfe sind `Knopf` mit `aria-pressed`
 * (Aktivzustand folgt der Auswahl, das liefert `zustand`), Tastatur siehe `useRovingLeiste`. Der Link-Dialog ist ein
 * `KleinesModal` S (D6b) mit `Textfeld`, nie `window.prompt`. Der Baustein prüft keine Rechte und speichert nichts.
 */
export function EditorLeiste({
  zustand,
  befehle,
  label = "Textformatierung",
}: {
  zustand: EditorLeistenZustand;
  befehle: EditorLeistenBefehle;
  /** Zugänglicher Name der Leiste. */
  label?: string;
}) {
  const roving = useRovingLeiste();
  // Der Dialog merkt sich die Adresse, die beim Öffnen am Link stand (leer = neuer Link).
  const [linkDialog, setLinkDialog] = useState<{ start: string } | null>(null);

  return (
    <>
      {/* Preline: forms/forms-sub-core-inputs-layouts/forms-cat-text-editors/rich-text-editor-card */}
      <div
        ref={roving.huelle}
        role="toolbar"
        aria-label={label}
        aria-orientation="horizontal"
        className={LEISTE_KLASSE}
        onKeyDown={roving.beiTaste}
        onFocus={roving.beiFokus}
      >
        <div className={GRUPPE_KLASSE} role="group" aria-label="Überschrift">
          <Werkzeug label="Überschrift 2" symbol={<SymUeberschrift2 />} aktiv={zustand.ueberschrift2} onClick={() => befehle.ueberschrift(2)} />
          <Werkzeug label="Überschrift 3" symbol={<SymUeberschrift3 />} aktiv={zustand.ueberschrift3} onClick={() => befehle.ueberschrift(3)} />
        </div>
        <div className={TRENNER_KLASSE} role="separator" aria-orientation="vertical" />
        <div className={GRUPPE_KLASSE} role="group" aria-label="Zeichenformat">
          <Werkzeug label="Fett" symbol={<SymFett />} aktiv={zustand.fett} onClick={befehle.fett} />
          <Werkzeug label="Kursiv" symbol={<SymKursiv />} aktiv={zustand.kursiv} onClick={befehle.kursiv} />
        </div>
        <div className={TRENNER_KLASSE} role="separator" aria-orientation="vertical" />
        <div className={GRUPPE_KLASSE} role="group" aria-label="Listen">
          <Werkzeug label="Liste" symbol={<SymListe />} aktiv={zustand.aufzaehlung} onClick={befehle.aufzaehlung} />
          <Werkzeug label="Nummerierung" symbol={<SymNummerierung />} aktiv={zustand.nummerierung} onClick={befehle.nummerierung} />
          <Werkzeug label="Aufgabenliste" symbol={<SymAufgabenliste />} aktiv={zustand.aufgabenliste} onClick={befehle.aufgabenliste} />
        </div>
        <div className={TRENNER_KLASSE} role="separator" aria-orientation="vertical" />
        <div className={GRUPPE_KLASSE} role="group" aria-label="Link">
          <Werkzeug
            label="Link"
            symbol={<SymLink />}
            aktiv={zustand.link}
            onClick={() => setLinkDialog({ start: befehle.linkAdresse() })}
          />
          {zustand.link && <Werkzeug label="Link entfernen" symbol={<SymLinkAus />} umschalter={false} onClick={befehle.linkEntfernen} />}
        </div>
        <div className={TRENNER_KLASSE} role="separator" aria-orientation="vertical" />
        <div className={GRUPPE_KLASSE} role="group" aria-label="Verlauf">
          <Werkzeug label="Rückgängig" symbol={<SymRueckgaengig />} umschalter={false} gesperrt={!zustand.kannRueckgaengig} onClick={befehle.rueckgaengig} />
          <Werkzeug label="Wiederholen" symbol={<SymWiederholen />} umschalter={false} gesperrt={!zustand.kannWiederholen} onClick={befehle.wiederholen} />
        </div>
      </div>

      <KleinesModal
        offen={linkDialog !== null}
        groesse="s"
        titel={linkDialog?.start ? "Link ändern" : "Link einfügen"}
        beschreibung="Die Adresse gilt für den markierten Text."
        beimSchliessen={() => setLinkDialog(null)}
        bestaetigenText="Übernehmen"
        onBestaetigen={(daten) => {
          const eingabe = String(daten.get("adresse") ?? "");
          const ergebnis = befehle.linkSetzen(eingabe);
          return ergebnis.ok ? { ok: true } : { ok: false, meldung: LINK_SAETZE[ergebnis.grund] };
        }}
      >
        <Textfeld
          key={linkDialog?.start ?? ""}
          label="Adresse"
          name="adresse"
          defaultValue={linkDialog?.start ?? ""}
          placeholder="https://"
          autoComplete="off"
          spellCheck={false}
          data-modal-fokus
        />
      </KleinesModal>
    </>
  );
}
